import { useState, useRef } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { QRCodeSVG } from "qrcode.react";
import { Download, Trash2 } from "lucide-react";
import { ToolPageLayout } from "@/components/ToolPageLayout";

export function QrCodeGenerator() {
  const [text, setText] = useState("https://example.com");
  const [size, setSize] = useState(256);
  const [fgColor, setFgColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  
  const qrRef = useRef<SVGSVGElement>(null);

  const downloadQR = () => {
    if (!qrRef.current) return;
    const svg = qrRef.current;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    
    img.onload = () => {
      canvas.width = size;
      canvas.height = size;
      if (ctx) {
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const pngFile = canvas.toDataURL("image/png");
        
        const downloadLink = document.createElement("a");
        downloadLink.download = "qrcode.png";
        downloadLink.href = `${pngFile}`;
        downloadLink.click();
      }
    };
    
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <ToolPageLayout
      title="QR Code Generator Online"
      description="Generate custom QR codes for URLs, text, or data instantly with our free QR code generator tool."
      h1="QR Code Generator"
      category="Dev Tools"
      categoryPath="/dev-tools"
      howToUse={[
        "Enter the text or URL you want to encode.",
        "Adjust size and colors if desired.",
        "Download the generated QR code as a PNG file."
      ]}
      faq={[
        { question: "Is this QR generator free?", answer: "Yes, it is 100% free to generate and download QR codes." }
      ]}
    >
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="space-y-2">
            <Label>Content (Text or URL)</Label>
            <Textarea
              placeholder="Enter URL or text to encode..."
              className="min-h-[120px]"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Size (px)</Label>
              <Input
                type="number"
                min="100"
                max="1000"
                value={size}
                onChange={(e) => setSize(Number(e.target.value) || 256)}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Foreground Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  className="w-12 h-10 p-1 cursor-pointer"
                  value={fgColor}
                  onChange={(e) => setFgColor(e.target.value)}
                />
                <Input
                  type="text"
                  value={fgColor}
                  onChange={(e) => setFgColor(e.target.value)}
                  className="font-mono"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Background Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  className="w-12 h-10 p-1 cursor-pointer"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                />
                <Input
                  type="text"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="font-mono"
                />
              </div>
            </div>
          </div>
          
          <div className="flex gap-2 pt-2">
            <Button onClick={() => setText("")} variant="ghost" className="text-red-600">
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </Button>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center space-y-6 bg-slate-50 border border-slate-200 rounded-lg p-8">
          {text ? (
            <>
              <div className="bg-white p-4 shadow-sm border border-slate-100 rounded-md">
                <QRCodeSVG
                  id="qr-code-svg"
                  value={text}
                  size={size}
                  fgColor={fgColor}
                  bgColor={bgColor}
                  level="M"
                  ref={qrRef}
                />
              </div>
              <Button onClick={downloadQR} className="w-full max-w-[256px]">
                <Download className="h-4 w-4 mr-2" />
                Download PNG
              </Button>
            </>
          ) : (
            <div className="text-slate-400 text-sm">Enter text to generate QR code</div>
          )}
        </div>
      </div>
    </ToolPageLayout>
  );
}
